import { useQuery } from '@tanstack/react-query'
import { NotebookPen, Plus } from 'lucide-react'
import { useState } from 'react'
import { notizenZurAufgabeQuery } from '../lib/mywork'
import { trpc } from '../lib/trpc'

/**
 * **Notizen beim Vorhaben und bei der Aufgabe** (Nils, 25.10.2026: *„aus work beim Vorhaben da
 * angezeigt und anlegbar sein. Generell bei Aufgaben natürlich auch"*).
 *
 * Zeigt, was im Notizbuch dazu steht – **nur aus Notizbüchern, die man lesen darf** (persönliche
 * sieht nur ihr Besitzer). „+ Notiz" öffnet im Notizbuch ein neues Blatt, das schon verknüpft ist
 * (`#neu-vorhaben-…` / `#neu-aufgabe-…`, `notizbuch/seite.tsx`), im Notizbuch der Welt, wenn man
 * dort schreiben darf. Ohne Notizbuch-Freigabe: nichts.
 */

interface Eintrag {
  notizbuchId: string
  notizId: string
  titel: string
  auszug: string
  datum: string
  hinweis?: string | undefined
}

function Liste({
  titel,
  eintraege,
  neuHref,
  leer,
}: {
  titel: string
  eintraege: Eintrag[]
  neuHref: string
  leer: string
}) {
  const [alle, setAlle] = useState(false)
  const sichtbar = alle ? eintraege : eintraege.slice(0, 5)
  return (
    <section className="mt-4">
      <div className="mb-1.5 flex items-center gap-2">
        <h4 className="text-xs font-bold">
          {titel}
          {eintraege.length > 0 ? (
            <span className="font-normal text-muted"> · {eintraege.length}</span>
          ) : (
            ''
          )}
        </h4>
        <a
          href={neuHref}
          className="ml-auto inline-flex items-center gap-1 text-[12.5px] font-semibold text-accent-deep hover:underline"
          title="Neues Blatt im Notizbuch, schon verknüpft"
        >
          <Plus className="size-[13px]" /> Notiz
        </a>
      </div>
      {eintraege.length === 0 ? (
        <p className="text-[12.5px] text-muted">{leer}</p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {sichtbar.map((n) => {
            const wann = new Date(n.datum).toLocaleDateString('de-DE', {
              day: 'numeric',
              month: 'numeric',
              year: '2-digit',
            })
            const inhalt = (
              <>
                <NotebookPen className="mt-0.5 size-[13px] shrink-0 text-muted" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-ink">
                    {n.titel || 'Ohne Titel'}
                  </span>
                  {n.auszug && (
                    <span className="line-clamp-1 text-[12px] text-muted">{n.auszug}</span>
                  )}
                </span>
                <span className="tabular shrink-0 text-[12px] text-muted">
                  {n.hinweis ? `${n.hinweis} · ` : ''}
                  {wann}
                </span>
              </>
            )
            // Optimistisch angelegte Einträge („neu-…") gibt es im Notizbuch noch nicht
            return (
              <li key={`${n.notizbuchId}:${n.notizId}`}>
                {n.notizId.startsWith('neu-') ? (
                  <div className="flex items-start gap-2 rounded-md px-2 py-1.5 opacity-60">
                    {inhalt}
                  </div>
                ) : (
                  <a
                    href={`/notizbuch#notiz-${n.notizbuchId}-${n.notizId}`}
                    className="flex items-start gap-2 rounded-md px-2 py-1.5 text-[13px] hover:bg-[#f3f2ef]"
                  >
                    {inhalt}
                  </a>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {eintraege.length > 5 && (
        <button
          type="button"
          onClick={() => setAlle(!alle)}
          className="mt-1 cursor-pointer text-[12px] font-semibold text-accent-deep"
        >
          {alle ? 'weniger' : `alle ${eintraege.length} zeigen`}
        </button>
      )}
    </section>
  )
}

function useFreigegeben(): boolean {
  const { data } = useQuery({
    queryKey: ['notizbuch', 'stand'],
    queryFn: () => trpc.notizbuch.stand.query(),
    staleTime: 5 * 60_000,
  })
  return !!data?.freigegeben
}

export function NotizenZumVorhaben({
  vorhabenId,
  workspaceId,
}: {
  vorhabenId: string
  workspaceId: string | null
}) {
  const freigegeben = useFreigegeben()
  const { data } = useQuery({
    queryKey: ['notizbuch', 'verknuepfung', 'vorhaben', vorhabenId],
    queryFn: () => trpc.notizbuch.verknuepfung.zumVorhaben.query({ vorhabenId }),
    enabled: freigegeben,
    staleTime: 30_000,
  })
  if (!freigegeben) return null
  return (
    <Liste
      titel="Notizen"
      eintraege={(data?.notizen ?? []).map((n) => ({
        ...n,
        hinweis: n.ueberMappe ? 'über Mappe' : undefined,
      }))}
      neuHref={`/notizbuch#neu-vorhaben-${vorhabenId}${workspaceId ? `-${workspaceId}` : ''}`}
      leer="Noch keine Notizen zu diesem Vorhaben. Im Blatt unter „Gehört zu“ verknüpfen oder hier eine neue anlegen."
    />
  )
}

export function NotizenZurAufgabeAbschnitt({
  aufgabeId,
  workspaceId,
}: {
  aufgabeId: string
  workspaceId: string | null
}) {
  const freigegeben = useFreigegeben()
  const { data = [] } = useQuery({ ...notizenZurAufgabeQuery(aufgabeId), enabled: freigegeben })
  if (!freigegeben) return null
  return (
    <Liste
      titel="Notizen"
      eintraege={data.map((n) => ({ ...n, hinweis: n.herkunft ? 'Herkunft' : undefined }))}
      neuHref={`/notizbuch#neu-aufgabe-${aufgabeId}${workspaceId ? `-${workspaceId}` : ''}`}
      leer="Noch keine Notizen zu dieser Aufgabe."
    />
  )
}
